import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Select from '../Select';
import Typography from '../Typography';
import Button from '../Button';
import HotkeysPreferences from '../HotkeysPreferences';
import { ButtonEnums } from '../Button';

const UserPreferences = ({
  availableLanguages,
  defaultLanguage,
  currentLanguage,
  disabled = false,
  hotkeyDefinitions,
  hotkeyDefaults,
  onCancel = () => {},
  onSubmit = () => {},
  onReset = () => {},
  hotkeysModule,
  servicesManager,
  tabs,
  activeTabIndex = 0,
}) => {
  const { t } = useTranslation('UserPreferencesModal');
  const [state, setState] = useState({
    isDisabled: disabled,
    hotkeyErrors: {},
    hotkeyDefinitions,
    language: currentLanguage,
    activeTabIndex,
  });

  const onSubmitHandler = () => {
    onSubmit(state);
  };

  const onResetHandler = () => {
    setState(state => ({
      ...state,
      language: defaultLanguage,
      hotkeyDefinitions: hotkeyDefaults,
      hotkeyErrors: {},
      isDisabled: disabled,
    }));
    onReset();
  };

  const onCancelHandler = () => {
    setState(state => ({
      ...state,
      hotkeyDefinitions,
    }));
    onCancel();
  };

  const onLanguageChangeHandler = value => {
    setState(state => ({ ...state, language: value }));
  };

  const onHotkeysChangeHandler = (id, definition, errors) => {
    setState(state => ({
      ...state,
      isDisabled: Object.values(errors).every(e => e !== undefined),
      hotkeyErrors: errors,
      hotkeyDefinitions: {
        ...state.hotkeyDefinitions,
        [id]: definition,
      },
    }));
  };

  const setActiveTabIndex = index => {
    setState(state => ({ ...state, activeTabIndex: index }));
  };

  const Section = ({ title, children }) => (
    <>
      <div className="mb-2 border-b-2 border-black">
        <Typography
          component="div"
          variant="inherit"
          color="primaryLight"
          className="flex pb-2 text-[16px] font-semibold !leading-[1.2]"
        >
          {title}
        </Typography>
      </div>
      <div className="mt-4 mb-8">{children}</div>
    </>
  );

  const getTabContent = () => {
    switch (state.activeTabIndex) {
      case 0:
        return (
          <>
            <Section title={t('General')}>
              <div className="flex min-w-[500px] flex-row items-center justify-center">
                <Typography
                  component="div"
                  variant="subtitle"
                  className="mr-5 h-full text-right"
                >
                  {t('Language')}
                </Typography>
                <Select
                  id="language-select"
                  isClearable={false}
                  onChange={onLanguageChangeHandler}
                  options={availableLanguages}
                  value={state.language}
                />
              </div>
            </Section>
            <Section title={t('Hotkeys')}>
              <HotkeysPreferences
                disabled={disabled}
                hotkeyDefinitions={state.hotkeyDefinitions}
                onChange={onHotkeysChangeHandler}
                errors={state.hotkeyErrors}
                hotkeysModule={hotkeysModule}
              />
            </Section>
          </>
        );
      default:
        // If there are additional tabs, render them
        if (tabs && tabs.length > 0 && tabs[state.activeTabIndex - 1]) {
          const TabComponent = tabs[state.activeTabIndex - 1].component;
          return <TabComponent servicesManager={servicesManager} />;
        }
        return null;
    }
  };

  // Calculate the number of tabs (default + custom)
  const tabCount = 1 + (tabs?.length || 0);

  return (
    <>
      {/* Tab Navigation */}
      {tabCount > 1 && (
        <div className="mb-4 flex border-b border-gray-300">
          <button
            className={`mr-2 py-2 px-4 ${
              state.activeTabIndex === 0
                ? 'border-b-2 border-blue-500 font-medium text-blue-500'
                : 'text-gray-600 hover:text-gray-800'
            }`}
            onClick={() => setActiveTabIndex(0)}
          >
            {t('Preferences')}
          </button>

          {tabs?.map((tab, index) => (
            <button
              key={index}
              className={`mr-2 py-2 px-4 ${
                state.activeTabIndex === index + 1
                  ? 'border-b-2 border-blue-500 font-medium text-blue-500'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
              onClick={() => setActiveTabIndex(index + 1)}
            >
              {t(tab.name)}
            </button>
          ))}
        </div>
      )}

      {/* Tab Content */}
      {getTabContent()}

      {/* Action Buttons */}
      <div className="flex flex-row justify-between">
        <Button
          type={ButtonEnums.type.secondary}
          onClick={onResetHandler}
          disabled={disabled}
        >
          {t('Reset to defaults')}
        </Button>
        <div className="flex flex-row">
          <Button
            type={ButtonEnums.type.secondary}
            onClick={onCancelHandler}
          >
            {t('Cancel')}
          </Button>
          <Button
            disabled={state.isDisabled}
            className="ml-2"
            onClick={onSubmitHandler}
          >
            {t('Save')}
          </Button>
        </div>
      </div>
    </>
  );
};

UserPreferences.propTypes = {
  disabled: PropTypes.bool,
  hotkeyDefaults: PropTypes.object.isRequired,
  hotkeyDefinitions: PropTypes.object.isRequired,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  onReset: PropTypes.func,
  hotkeysModule: PropTypes.shape({
    initialize: PropTypes.func.isRequired,
    pause: PropTypes.func.isRequired,
    unpause: PropTypes.func.isRequired,
    startRecording: PropTypes.func.isRequired,
    record: PropTypes.func.isRequired,
  }).isRequired,
  availableLanguages: PropTypes.array,
  defaultLanguage: PropTypes.object,
  currentLanguage: PropTypes.object,
  servicesManager: PropTypes.object,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      component: PropTypes.elementType.isRequired,
    })
  ),
  activeTabIndex: PropTypes.number,
};

export default UserPreferences;
